import { config } from "../Environment";
import type { Answers, SeedId, Turn } from "../lib/startFlow";
import { getVisitorId } from "../lib/startFlow";

export type SeedText = {
  title: string;
  questions: Record<string, { label: string; a?: string; b?: string; placeholder?: string }>;
  angles: Record<string, Record<string, any> & { title: string; body?: string }>;
  relief: string[];
  steps: string[];
  deeperIntro: string;
  checkin: { subject: string; body: string; q1: string; q2: string };
};

export type SeedView = {
  id: SeedId;
  version: number;
  locale: "en" | "he" | "de";
  questions: { id: string; kind: "two_fields" | "short_text" | "chips_plus_text"; optional?: boolean }[];
  angles: { id: string; ai: boolean; interaction: string }[];
  deeper: { maxTurns: number };
  text: SeedText;
};

export type AngleResponse =
  | { angleId: string; content?: string; fallback?: boolean }
  | { done: true }
  | { distress: true }
  | { fallback: true; reason: "rate_limited" };

export type DeeperResponse = { text: string; final: boolean } | { fallback: true } | { distress: true };

async function publicPost<T>(path: string, body: object): Promise<T> {
  const res = await fetch(`${config.API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...body, visitorId: getVisitorId() }),
  });
  if (res.status === 429) return (await res.json()) as T;
  if (!res.ok) throw new Error(`${path} failed: ${res.status}`);
  return res.json();
}

export async function getSeed(id: SeedId, locale: string): Promise<SeedView> {
  const res = await fetch(`${config.API_BASE}/api/start/seeds/${id}?locale=${encodeURIComponent(locale)}`);
  if (!res.ok) throw new Error(`seed ${id} failed: ${res.status}`);
  return res.json();
}

export const routeText = (text: string, locale: string) =>
  publicPost<{ seedId: SeedId | null; distress?: true; fallback?: true }>("/api/start/route", { text, locale });

export const getAngle = (req: { seedId: SeedId; answers: Answers; seenAngleIds: string[]; locale: string }) =>
  publicPost<AngleResponse>("/api/start/angle", req);

export const deeperTurn = (req: { seedId: SeedId; answers: Answers; helpfulAngleId?: string; turns: Turn[]; locale: string }) =>
  publicPost<DeeperResponse>("/api/start/deeper", req);

async function authedJson<T>(retryFetch: Function, path: string, init?: RequestInit): Promise<T> {
  const res = await retryFetch(`${config.API_BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    ...init,
  });
  if (!res.ok) throw new Error(`${path} failed: ${res.status}`);
  return res.json();
}

export type KeepBody = {
  flowId: string; seedId: SeedId; angleId?: string; cardText: string;
  step?: { text: string; when: "today" | "week" }; src?: string; c?: string; locale: string;
};

export const keepFlow = (retryFetch: Function, body: KeepBody) =>
  authedJson<{ flowId: string; insightId: string; actionId: string | null }>(retryFetch, "/api/start/keep", {
    method: "POST",
    body: JSON.stringify(body),
  });

export type FlowView = {
  flowId: string; seedId: SeedId; angleId?: string; locale: string;
  insight: { _id: string; content: string } | null;
  action: { _id: string; content: string } | null;
  checkinCompletedAt: number | null; fiPwf: string | null;
  checkin: { q1: string; q2: string };
};

export const getFlow = (retryFetch: Function, flowId: string) =>
  authedJson<FlowView>(retryFetch, `/api/start/flow/${encodeURIComponent(flowId)}`);

export const submitCheckin = (retryFetch: Function, body: { flowId: string; outcome: string; learned: string }) =>
  authedJson<{ learnInsightId: string }>(retryFetch, "/api/start/checkin", { method: "POST", body: JSON.stringify(body) });

export const attachProject = (retryFetch: Function, body: { flowId: string; projectId: string }) =>
  authedJson<{ ok: true }>(retryFetch, "/api/start/attach-project", { method: "POST", body: JSON.stringify(body) });
